import * as React from 'react';
import { Route, Switch } from 'react-router-dom';
import { getCookie, PRIMARY_JWT_COOKIE_NAME } from './constants';
import MenuBar from './routes/MenuBar';
import LoginView from './security/LoginView';
import { SessionStorageKey } from './types';
import ErrorBoundary from './widgets/ErrorBoundary';
import GlobalStatusReporter from './widgets/GlobalStatusReporter';
import MaskedLoadingIndicator from './widgets/MaskedLoadingIndicator';
import UnhandledError from './widgets/UnhandledError';
import { AwthProvider, AwthProviderProps } from './awth/AwthProvider';
import { AppProps, AwthenticatedRoutes } from './App';

export type AppWithLoginProps = AppProps & AwthProviderProps;

export const AppWithLoginPage: React.FC<AppWithLoginProps> = ({
  isSwitchingCustomer,
  clearGlobalStatus,
  globalStatus,
  username,
  useFrontendAuth0,
  rolesAndServices,
  auth0Config,
  loginConfig,
}) => {
  const loggedIn = !!username;

  let app = (
    <div>
      <MenuBar
        isAuth0Logout={useFrontendAuth0}
        rolesAndServices={rolesAndServices}
      />
      <GlobalStatusReporter
        status={globalStatus}
        onDismiss={clearGlobalStatus}
      />
      <ErrorBoundary errorComponent={<UnhandledError />}>
        {loggedIn ? (
          <AwthenticatedRoutes />
        ) : (
          <UnawthenticatedRoutes
            isSwitchingCustomer={isSwitchingCustomer}
            useFrontendAuth0={useFrontendAuth0}
          />
        )}
      </ErrorBoundary>
    </div>
  );

  if (useFrontendAuth0) {
    app = (
      <AwthProvider auth0Config={auth0Config} loginConfig={loginConfig}>
        {app}
      </AwthProvider>
    );
  }

  return <>{app}</>;
};

// What you see when you're not logged in
export const UnawthenticatedRoutes = ({
  isSwitchingCustomer,
  useFrontendAuth0,
}: {
  isSwitchingCustomer: boolean;
  useFrontendAuth0: boolean;
}) => {
  const primaryToken =
    window.sessionStorage.getItem(SessionStorageKey.PRIMARY_TOKEN) ||
    getCookie(PRIMARY_JWT_COOKIE_NAME);
  if (primaryToken) {
    return (
      <MaskedLoadingIndicator
        message={isSwitchingCustomer ? 'Switching customer' : 'Connecting'}
      />
    );
  }
  if (useFrontendAuth0) {
    return <MaskedLoadingIndicator message="Logging In" />;
  }
  return (
    <Switch>
      <Route component={LoginView} />
    </Switch>
  );
};

export default AppWithLoginPage;
